import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Reveal, Item } from "@/components/Reveal";
import { SITE, waLink, waLinkFor } from "@/lib/site";
import { loadBrief, clearBrief, calUrlWithBrief } from "@/lib/leadBrief";
import { MessageCircle, Check } from "lucide-react";

// Same-origin Cloudflare Pages Function: no backend URL to configure.
const API = "/api";

// Absent until Turnstile is set up; the form works either way.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export default function Book() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    repetitive_job: "",
    website: "", // honeypot, must stay empty
  });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  /* Whatever they answered in the opportunity finder, carried across so the
     call does not start from zero. Read once: a later restart of the finder
     should not blank the page they are already looking at. */
  const [brief, setBrief] = useState(() => loadBrief());

  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const callbackWaLink = `https://wa.me/${SITE.waNumber}?text=${encodeURIComponent(
    `Hi AUT labs, please call me back.\nName: ${form.name}\nCompany: ${form.company}\nPhone: ${form.phone}\nWhat is eating our week: ${form.repetitive_job}` +
      (brief ? `\n\n${brief.text}` : "")
  )}`;
  const bookWaLink = brief ? waLinkFor(`Hi AUT labs. ${brief.text}`) : waLink;
  const dropBrief = () => { clearBrief(); setBrief(null); };
  const submitLabel = status === "sending"
    ? "Sending…"
    : status === "error"
      ? "Try again"
      : "Ask for a callback";

  // A finished diagnostic already answered "what keeps eating the week".
  // Prefilled, not locked: they can still rewrite it.
  useEffect(() => {
    if (!brief?.top?.length) return;
    setForm((current) =>
      current.repetitive_job
        ? current
        : { ...current, repetitive_job: brief.top.map((o) => o.problem).join("; ") }
    );
  }, [brief]);

  // Load the Turnstile script only on this page, and only when configured.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    window.autlabsTurnstileDone = (token) => {
      window.__autlabsTurnstileToken = token;
    };
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
    return () => {
      s.remove();
      delete window.autlabsTurnstileDone;
    };
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/audit-request`, {
        ...form,
        diagnostic: brief?.text || "",
        turnstile_token: window.__autlabsTurnstileToken || "",
        source_page: sessionStorage.getItem("bookingSource") || (brief ? "opportunity-finder" : "direct"),
      });
      setStatus("done");
    } catch (error) {
      // Status only. The full axios error carries request detail we do not want
      // sitting in a visitor's console.
      console.error("Callback request failed", error?.response?.status || "network");
      setStatus("error");
    }
  };

  return (
    <div data-testid="page-book">
      <Seo
        title="Book a 15-minute call · AUT labs"
        description="Talk to AUT labs about repetitive work, custom software, ERP and CRM systems, automation or AI. No technical brief needed."
      />

      <section className="border-b border-ink col-rules">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-9 md:py-11">
          <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="md:col-span-7">
              <Item as="p" className="eyebrow text-ember-deep">Book a 15-minute call</Item>
              <h1 className="display text-[42px] md:text-[68px] leading-[0.96] mt-4 text-cobalt">
                A useful 15 minutes.
              </h1>
            </div>
            <Item as="p" className="md:col-span-5 text-lg text-ink/80 max-w-[42ch]">
              Show us what keeps getting repeated, delayed or chased. We will ask a few
              plain questions and tell you the clearest next step.
            </Item>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-ink" data-testid="cal-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-7 md:py-9">
          <div className="grid grid-cols-1 md:grid-cols-12 border-t border-l border-ink">
            <div className="md:col-span-8 border-r border-b border-ink p-4 md:p-6" data-testid="cal-embed">
              <div className="flex flex-wrap justify-between gap-3 items-baseline mb-4">
                <h2 className="display text-[30px] md:text-[38px] leading-none">Choose a time.</h2>
                <p className="text-sm text-ink/60">Free call · no deck · nothing to sign</p>
              </div>

              {brief && (
                <div className="mb-4 border border-ink p-4 md:p-5" data-testid="brief-attached">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <p className="eyebrow inline-flex items-center gap-2">
                      <Check size={14} aria-hidden="true" /> Your answers are attached
                    </p>
                    <button type="button" onClick={dropBrief} className="ember-link text-[13px] text-ink/55" data-testid="brief-clear">
                      Remove
                    </button>
                  </div>
                  <p className="mt-3 text-[15px] text-ink/80">
                    We already have your <b className="font-semibold">{brief.industry.toLowerCase()}</b> setup
                    {brief.top.length ? <> and the top {brief.top.length} opportunities it flagged</> : null}.
                    The 15 minutes start on your workflow, not on background questions.
                  </p>
                  {brief.top.length > 0 && (
                    <ul className="mt-3 flex flex-wrap gap-2">
                      {brief.top.map((o) => <li key={o.problem} className="chip">{o.problem}</li>)}
                    </ul>
                  )}
                </div>
              )}

              <div
                className="w-full md:w-[90%] mx-auto border border-ink"
                data-testid="booking-window"
              >
                <iframe
                  title="Book a 15-minute call, AUT labs booking calendar"
                  src={calUrlWithBrief(SITE.calEmbed, brief)}
                  className="w-full h-[504px] border-0"
                  loading="eager"
                />
              </div>
            </div>

            <aside className="md:col-span-4 border-r border-b border-ink" aria-label="Other ways to contact AUT labs">
              <div className="p-5 md:p-6 border-b border-ink" data-testid="contact-column">
                <p className="eyebrow text-ink/60">Rather talk now?</p>
                <a
                  href={`tel:${SITE.phoneTel}`}
                  className="display text-[32px] md:text-[38px] leading-none block ember-link mt-4"
                  data-testid="book-phone"
                >
                  {SITE.phoneDisplay}
                </a>
                <div className="mt-5 flex flex-wrap gap-4 items-center">
                  <a
                    href={bookWaLink}
                    target="_blank"
                    rel="noreferrer"
                    className="pill pill-ember"
                    data-testid="book-wa"
                  >
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                  <a href={`mailto:${SITE.email}`} className="ember-link" data-testid="book-email">
                    {SITE.email}
                  </a>
                </div>
              </div>

              <div className="p-5 md:p-6" data-testid="form-column">
                {status === "done" ? (
                  <div data-testid="form-success" className="min-h-[260px] flex items-center">
                    <p className="display text-[30px] md:text-[38px] leading-[1.1]">
                      Got it. We'll call you today.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="audit-form">
                    <p className="eyebrow text-ink/60 mb-5">Prefer a callback?</p>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="name" className="eyebrow block mb-2">Name</label>
                        <input
                          id="name" required value={form.name} onChange={update("name")}
                          autoComplete="name"
                          className="w-full bg-transparent border border-ink px-3 py-2.5 outline-none focus:bg-ink/[0.03] focus:ring-2 focus:ring-ember"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="eyebrow block mb-2">Company</label>
                        <input
                          id="company" required value={form.company} onChange={update("company")}
                          autoComplete="organization"
                          className="w-full bg-transparent border border-ink px-3 py-2.5 outline-none focus:bg-ink/[0.03] focus:ring-2 focus:ring-ember"
                          data-testid="input-company"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="eyebrow block mb-2">Phone</label>
                        <input
                          id="phone" type="tel" required value={form.phone} onChange={update("phone")}
                          autoComplete="tel"
                          className="w-full bg-transparent border border-ink px-3 py-2.5 outline-none focus:bg-ink/[0.03] focus:ring-2 focus:ring-ember"
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <label htmlFor="job" className="eyebrow block mb-2">What keeps eating the week?</label>
                        <textarea
                          id="job" rows={2} required value={form.repetitive_job} onChange={update("repetitive_job")}
                          className="w-full bg-transparent border border-ink px-3 py-2.5 outline-none focus:bg-ink/[0.03] focus:ring-2 focus:ring-ember resize-none"
                          data-testid="input-job"
                        />
                      </div>
                    </div>

                    {/* Honeypot. Hidden from people, irresistible to form bots.
                        Not display:none, which some bots detect and skip. */}
                    <div aria-hidden="true" className="absolute left-[-9999px] top-auto w-px h-px overflow-hidden">
                      <label htmlFor="website">Website</label>
                      <input
                        id="website" name="website" type="text" tabIndex={-1} autoComplete="off"
                        value={form.website} onChange={update("website")}
                      />
                    </div>

                    {/* Turnstile renders here once REACT_APP_TURNSTILE_SITE_KEY is set.
                        Without a key the widget is absent and the server skips the check,
                        so the form keeps working before Turnstile is configured. */}
                    {TURNSTILE_SITE_KEY ? (
                      <div
                        className="cf-turnstile mt-5"
                        data-sitekey={TURNSTILE_SITE_KEY}
                        data-callback="autlabsTurnstileDone"
                        data-theme="light"
                      />
                    ) : null}

                    <button
                      type="submit"
                      disabled={status === "sending"}
                      className="pill pill-cobalt mt-5 disabled:opacity-60"
                      data-testid="submit-btn"
                    >
                      {submitLabel}
                    </button>

                    <p className="text-[12px] leading-relaxed text-ink/55 mt-4">
                      We use these details only to respond to this request. See our{" "}
                      <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link>.
                    </p>

                    {status === "error" && (
                      <div className="mt-4 border-l-4 border-ember-deep bg-ember/10 px-4 py-3" data-testid="form-error">
                        <p className="text-ink font-medium leading-snug">
                          We could not save your callback request. Your details are still here.
                        </p>
                        <p className="text-sm text-ink/70 mt-2">
                          Please try again, or{" "}
                          <a
                            href={callbackWaLink}
                            target="_blank"
                            rel="noreferrer"
                            className="underline underline-offset-2 text-ember-deep"
                            data-testid="form-error-wa"
                          >
                            send the same request on WhatsApp
                          </a>.
                        </p>
                      </div>
                    )}
                  </form>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
